import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "@/lang";
import { MaskLines, Reveal, Crosshair, EASE } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { CertBadges } from "@/components/CertBadges";
import { CATS, ITEMS } from "@/catalogData";
import { PHONE_TEL, EMAIL } from "@/content";

const HERO_BG =
    "https://images.unsplash.com/photo-1676210134188-4c05dd172f89?q=85&w=2000&auto=format&fit=crop";
const CTA_BG =
    "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=85&w=2000&auto=format&fit=crop";

const RANGES = [
    {
        key: "LIFESTYLE",
        group: "PLUMBING",
        img: "SEBM",
        label: { en: "LIFESTYLE PRODUCTS", es: "PRODUCTOS LIFESTYLE" },
        title: { en: ["Bath &", "lifestyle."], es: ["Baño y", "estilo de vida."] },
        line: {
            en: "Mixers, showers, shattaf and seat covers — the Eco, Classic, Elegant, Royal, Futura and Europa families, one finish standard.",
            es: "Mezcladoras, duchas, shattaf y tapas de inodoro — las familias Eco, Classic, Elegant, Royal, Futura y Europa, un solo estándar de acabado.",
        },
        familyChips: true,
        cats: ["MIXERS", "SHOWERS", "SHATTAFF", "SEAT COVERS"],
        sample: ["SEBM", "SESH5R120", "SCSW1", "SUS11"],
    },
    {
        key: "PUMPS",
        group: "PUMPS",
        img: "CENTRIFUGAL-PUMP",
        label: { en: "PUMPS", es: "BOMBAS" },
        title: { en: ["Pumps for", "every source."], es: ["Bombas para", "cada fuente."] },
        line: {
            en: "Domestic, commercial, submersible and pool pumps — plus float switches and automatic controllers.",
            es: "Bombas domésticas, comerciales, sumergibles y de piscina — además de flotadores, controladores automáticos y tuberías de columna.",
        },
        cats: ["DOMESTIC PUMPS", "COMMERCIAL PUMPS", "SUBMERSIBLE", "POOL", "PUMP ACCESSORIES"],
        sample: ["CENTRIFUGAL-PUMP", "VERTICAL-MULTISTAGE-PUMPS", "SUBMERSIBLE-OPEN-WELL-PUMP-1", "AUTOMATIC-PUMP-CONTROLLER-1"],
    },
    {
        key: "PLUMBING",
        group: "PLUMBING",
        img: "SP20",
        label: { en: "PLUMBING", es: "PLOMERÍA" },
        title: { en: ["Behind", "every wall."], es: ["Detrás de", "cada pared."] },
        line: {
            en: "PPR pipes and fittings, brass valves, flush fittings and the ancillaries that finish a job.",
            es: "Tuberías y fittings PPR, válvulas de latón, mecanismos de cisterna y los accesorios que terminan el trabajo.",
        },
        cats: ["PPR SYSTEM", "VALVES", "FLUSH FITTINGS", "ANCILLARIES"],
        sample: ["SP20", "SBV", "SDB12TP21S", "SSLBT"],
    },
    {
        key: "ELECTRICAL",
        group: "ELECTRICAL",
        img: "S200SF14",
        label: { en: "ELECTRICAL", es: "ELÉCTRICO" },
        title: { en: ["Power, made", "practical."], es: ["Energía", "práctica."] },
        line: {
            en: "Induction motors, storage water heaters, ventilation fans, cable reels and copper cable.",
            es: "Motores de inducción, calentadores de agua, ventiladores, carretes de cable y cable de cobre.",
        },
        cats: ["MOTORS", "WATER HEATERS", "FANS", "CABLE REELS", "COPPER CABLE"],
        sample: ["S200SF14", "STORAGE-WATER-HEATER", "VENTILATING-FAN-DOMESTIC-1", "CABLE-REEL-METAL"],
    },
];

function Hero() {
    const { c } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

    return (
        <section ref={ref} className="relative flex min-h-[100svh] flex-col overflow-hidden" data-testid="hero-section">
            <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
                <img src={HERO_BG} alt="" className="h-full w-full scale-110 object-cover object-center" />
                <div className="absolute inset-0 bg-ink/60" />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/20 to-ink" />
            </motion.div>

            <div className="container-x flex flex-1 flex-col justify-end pb-16 pt-32">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                    className="mb-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky"
                >
                    <Crosshair className="text-sky" />
                    <span data-testid="hero-eyebrow">{c.hero.eyebrow}</span>
                    <span className="hidden h-px w-16 bg-paper/30 sm:block" />
                    <span className="hidden text-paper/70 sm:block">{c.hero.since}</span>
                </motion.div>

                <h1 className="text-[clamp(3rem,9vw,7.5rem)] font-light leading-[1.02] tracking-[-0.02em] text-paper">
                    <MaskLines mount lines={c.hero.lines} step={0.13} delay={0.25} />
                </h1>

                <div className="mt-10 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
                        className="max-w-xl text-base leading-relaxed text-paper/75 sm:text-lg"
                        data-testid="hero-sub"
                        dangerouslySetInnerHTML={{ __html: c.hero.sub }}
                    />

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 1 }}
                        className="flex flex-col gap-3 sm:flex-row"
                    >
                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="hero-phone-cta"
                            className="flex items-center justify-center gap-3 rounded-full bg-paper px-8 py-4 text-base font-semibold text-ink transition-all duration-200 hover:bg-sky"
                        >
                            <Phone className="h-4 w-4" strokeWidth={2.5} />
                            {c.hero.call}
                        </a>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="hero-email-cta"
                            className="flex items-center justify-center gap-3 rounded-full border border-paper/35 px-8 py-4 text-base font-semibold text-paper backdrop-blur-sm transition-all duration-200 hover:border-paper hover:bg-paper/10"
                        >
                            <Mail className="h-4 w-4" />
                            {c.hero.email}
                        </a>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="border-t border-paper/15 bg-ink/30 backdrop-blur-md"
            >
                <div className="container-x flex flex-wrap items-center justify-between gap-3 py-4 font-mono text-[10px] uppercase tracking-[0.22em] text-paper/70 sm:text-[11px]">
                    <span data-testid="hero-standby-badge" className="flex items-center gap-2.5">
                        <span className="h-2 w-2 animate-pulse-dot rounded-full bg-sky" />
                        {c.hero.standby}
                    </span>
                    <span className="hidden md:block">{c.hero.coords}</span>
                    <span className="flex items-center gap-2" data-testid="hero-scroll-hint">
                        {c.hero.scroll}
                        <motion.span
                            animate={{ y: [0, 5, 0] }}
                            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                            className="block h-3 w-px bg-sky"
                        />
                    </span>
                </div>
            </motion.div>
        </section>
    );
}

function Statement() {
    const { c } = useLang();
    return (
        <section className="container-x grid gap-12 py-28 md:grid-cols-12 md:py-40" data-testid="manifesto-section">
            <div className="md:col-span-8">
                <Reveal>
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                        <span className="h-px w-10 bg-sky-text/50" />
                        {c.manifesto.label}
                    </div>
                </Reveal>
                <h2 className="mt-8 text-4xl font-light leading-[1.05] tracking-[-0.02em] text-ink sm:text-6xl lg:text-7xl">
                    <MaskLines lines={c.manifesto.big} step={0.1} />
                </h2>
            </div>
            <div className="flex flex-col justify-end md:col-span-4">
                <Reveal delay={0.15}>
                    <p className="text-base leading-relaxed text-ink-soft">{c.manifesto.body}</p>
                    <Link
                        to="/about"
                        data-testid="manifesto-about-link"
                        className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.1em] text-sky-text"
                    >
                        <span dangerouslySetInnerHTML={{ __html: c.manifesto.link }} />
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

function UsTrade() {
    const { c } = useLang();
    const t = c.usTrade;
    return (
        <section className="border-t border-line bg-pure py-24 md:py-32" data-testid="us-trade-section">
            <div className="container-x">
                <Reveal>
                    <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-text">
                        <span className="h-px w-10 bg-sky-text/50" />
                        {t.label}
                    </div>
                </Reveal>
                <h2 className="mt-6 max-w-3xl text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-6xl">
                    <MaskLines lines={t.title} />
                </h2>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-soft">{t.body}</p>
                </Reveal>
                <div className="mt-14 grid gap-5 md:grid-cols-3">
                    {t.cards.map((card, i) => (
                        <Reveal key={card.t} delay={i * 0.08}>
                            <div
                                className="flex h-full flex-col rounded-3xl border border-line bg-paper p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-soft"
                                data-testid={`us-trade-card-${i + 1}`}
                            >
                                <h3 className="text-xl font-normal tracking-tight text-ink">{card.t}</h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-soft">{card.d}</p>
                                <a
                                    href={card.href}
                                    data-testid={`us-trade-link-${i + 1}`}
                                    className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink transition-colors hover:text-sky-text"
                                >
                                    {card.cta}
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </a>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function StickyRanges() {
    const { c, lang } = useLang();
    const [active, setActive] = useState(0);
    const label = (r) => (lang === "es" ? r.label.es : r.label.en);
    const title = (r) => (lang === "es" ? r.title.es : r.title.en);
    const line = (r) => (lang === "es" ? r.line.es : r.line.en);
    const chips = (r) =>
        r.familyChips
            ? c.products.families
            : r.cats.map((key) => {
                  const k = CATS.find((x) => x.key === key);
                  return lang === "es" ? k.es : k.en;
              });
    const skus = (r) => ITEMS.filter((i) => r.cats.includes(i.cat)).length;

    return (
        <section className="border-t border-line bg-pure" data-testid="catalogue-section">
            <h2 className="sr-only">{lang === "es" ? "Gamas de productos" : "Product ranges"}</h2>
            <div className="container-x grid gap-10 lg:grid-cols-2 lg:gap-16">
                <div className="hidden lg:block">
                    <div className="sticky top-0 flex h-screen items-center">
                        <div
                            className="relative h-[78vh] w-full overflow-hidden rounded-[2rem] border border-line bg-pure shadow-soft"
                            data-testid="catalogue-image-panel"
                        >
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={active}
                                    src={`/products/${RANGES[active].img}.png`}
                                    alt={label(RANGES[active])}
                                    initial={{ opacity: 0, scale: 1.05 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.98 }}
                                    transition={{ duration: 0.5, ease: EASE }}
                                    className="absolute inset-0 h-full w-full object-contain p-16"
                                />
                            </AnimatePresence>
                            <div className="absolute bottom-6 left-6 flex items-center gap-3 rounded-full border border-line bg-pure/90 px-4 py-2 backdrop-blur">
                                <span className="h-2 w-2 rounded-full bg-sky-text" />
                                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink">
                                    {label(RANGES[active])} · {skus(RANGES[active])} SKUs
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="py-10 lg:py-0">
                    {RANGES.map((r, i) => (
                        <motion.div
                            key={r.key}
                            onViewportEnter={() => setActive(i)}
                            viewport={{ margin: "-45% 0px -45% 0px" }}
                            className="flex min-h-[60vh] flex-col justify-center border-b border-line py-14 lg:min-h-[92vh]"
                            data-testid={`catalogue-block-${i + 1}`}
                        >
                            <div className="mb-6 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-mist">
                                <span className="text-sky-text">
                                    {String(i + 1).padStart(2, "0")} / {String(RANGES.length).padStart(2, "0")}
                                </span>
                                <span>{skus(r)} SKUs</span>
                            </div>
                            <div className="mb-8 overflow-hidden rounded-2xl border border-line bg-paper lg:hidden">
                                <img
                                    src={`/products/${r.img}.png`}
                                    alt={label(r)}
                                    className="mx-auto h-56 w-auto object-contain p-6"
                                />
                            </div>
                            <MaskLines
                                lines={title(r)}
                                className="text-3xl font-light tracking-tight text-ink sm:text-5xl"
                            />
                            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-soft">{line(r)}</p>
                            <Reveal delay={0.1}>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {chips(r).map((chip) => (
                                        <span
                                            key={chip}
                                            className="rounded-full border border-line px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-soft"
                                        >
                                            {chip}
                                        </span>
                                    ))}
                                </div>
                            </Reveal>
                            <div className="mt-8 grid grid-cols-4 gap-3 sm:max-w-md" data-testid={`story-thumbs-${i + 1}`}>
                                {r.sample.map((code) => {
                                    const item = ITEMS.find((it) => it.code === code);
                                    if (!item) return null;
                                    return (
                                        <Link
                                            key={code}
                                            to={`/products?cat=${encodeURIComponent(item.cat)}`}
                                            data-testid={`story-thumb-${r.key}-${code}`}
                                            className="group overflow-hidden rounded-xl border border-line bg-paper"
                                            title={lang === "es" ? item.nameEs : item.name}
                                        >
                                            <img
                                                src={`/products/${code.replace(/\//g, ".")}.png`}
                                                alt={lang === "es" ? item.nameEs : item.name}
                                                loading="lazy"
                                                className="h-16 w-full object-contain p-1.5 transition-transform duration-500 group-hover:scale-110 sm:h-20 sm:p-2"
                                            />
                                        </Link>
                                    );
                                })}
                            </div>
                            <Link
                                to={`/products?group=${r.group}`}
                                data-testid={`catalogue-link-${i + 1}`}
                                className="group mt-8 inline-flex w-fit items-center gap-2 rounded-full border border-line px-6 py-3 text-sm font-semibold text-ink transition-all duration-200 hover:border-ink hover:bg-ink hover:text-paper"
                            >
                                {c.productsStrip.link}
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Closing() {
    const { c, lang } = useLang();
    return (
        <section className="border-t border-line bg-pure py-20 md:py-24" data-testid="home-closing">
            <h2 className="sr-only">{lang === "es" ? "Por qué Star USA — cifras y certificaciones" : "Why Star USA — numbers and certifications"}</h2>
            <div className="container-x">
                <div className="grid grid-cols-2 gap-y-10 border-b border-line pb-14 md:grid-cols-4" data-testid="closing-stats">
                    {c.stats.map((s, i) => (
                        <div key={s.label} className="px-4 text-center" data-testid={`stat-cell-${i + 1}`}>
                            <Reveal delay={i * 0.07}>
                                <p className="text-4xl font-light tracking-tight text-ink sm:text-5xl">
                                    <span className="tabular-nums">{s.n.toLocaleString("en-US")}</span>
                                    <span className="text-sky-text">{s.suffix}</span>
                                </p>
                                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mist sm:text-[11px]">
                                    {s.label}
                                </p>
                            </Reveal>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col items-center gap-6 pt-14 text-center" data-testid="closing-certs">
                    <Reveal>
                        <p className="max-w-2xl text-base leading-relaxed text-ink-soft">{c.certs.line}</p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <CertBadges />
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function ParallaxCta() {
    const { c } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

    return (
        <section ref={ref} className="relative flex min-h-[85vh] items-center overflow-hidden" data-testid="cta-band">
            <motion.img
                src={CTA_BG}
                alt=""
                style={{ y }}
                className="absolute inset-0 h-[124%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-ink/65" />
            <div className="container-x relative py-28 text-center">
                <Reveal>
                    <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky">{c.cta.label}</p>
                </Reveal>
                <MaskLines
                    lines={c.cta.big}
                    className="mx-auto mt-6 text-[clamp(2.8rem,8vw,7rem)] font-light leading-[1.02] tracking-[-0.02em] text-paper"
                    step={0.12}
                />
                <Reveal delay={0.2}>
                    <p className="mx-auto mt-6 max-w-md text-base text-paper/75">{c.cta.sub}</p>
                    <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <a
                            href={`tel:${PHONE_TEL}`}
                            data-testid="cta-band-phone"
                            className="flex items-center gap-3 rounded-full bg-paper px-8 py-4 text-lg font-semibold text-ink transition-all duration-200 hover:bg-sky"
                        >
                            <Phone className="h-5 w-5" strokeWidth={2.5} />
                            {c.cta.call}
                        </a>
                        <a
                            href={`mailto:${EMAIL}`}
                            data-testid="cta-band-email"
                            className="flex items-center gap-3 rounded-full border border-paper/35 px-8 py-4 font-mono text-sm text-paper transition-colors hover:border-paper hover:bg-paper/10"
                        >
                            <Mail className="h-4 w-4 text-sky" />
                            {c.cta.email}
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export default function Home() {
    const { c } = useLang();
    return (
        <>
            <Hero />
            <Marquee items={c.marquee} />
            <Statement />
            <UsTrade />
            <StickyRanges />
            <Closing />
            <ParallaxCta />
        </>
    );
}
